import {
  Box,
  Text,
  VStack,
  Button,
  Switch,
  Alert,
  HStack,
  SimpleGrid,
} from '@chakra-ui/react';
import { useState } from 'react';

import { toaster } from '#src/components/ui/toaster';
import {
  useAppConfig,
  SIDEBAR_TOGGLE_POSITIONS,
  type SidebarTogglePosition,
} from '#src/providers/app-config-provider';
import { useCurrentUser, useUser } from '#src/providers/user-provider';

export function SettingsPage() {
  const user = useCurrentUser();
  const { logout } = useUser();
  const { config, setIsLocal, clearField, setSidebarTogglePosition } =
    useAppConfig();
  const [settings, setSettings] = useState({
    autoSave: true,
    darkMode: false,
    notifications: true,
  });

  const selectedEndpoint = config.isLocal ? 'local' : 'remote';

  const handleEndpointChange = (endpointType: 'local' | 'remote') => {
    const isLocal = endpointType === 'local';
    setIsLocal(isLocal);
    toaster.create({
      title: 'Success',
      description: `API endpoint changed to ${endpointType === 'remote' ? 'Remote' : 'Local'}`,
      type: 'success',
      duration: 3000,
      closable: true,
    });
  };

  const handleSave = () => {
    // Save settings logic here
    // TODO: Implement settings persistence
  };

  return (
    <Box>
      <Box bg="white" shadow="lg" borderRadius="lg" p={6}>
        <VStack gap={6} align="stretch">
          <Alert.Root status="info">
            <Alert.Indicator />
            <Alert.Title>
              Settings are saved locally in your browser.
            </Alert.Title>
          </Alert.Root>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              User
            </Text>
            <Text fontSize="xs" color="gray.500" mb={2}>
              Signed in as
            </Text>
            <HStack gap={3} align="center">
              <Text fontSize="md" fontWeight="medium" color="gray.800">
                {user?.email}
              </Text>
              <Button
                variant="outline"
                colorScheme="gray"
                size="sm"
                onClick={() => {
                  void logout().then(() => {
                    toaster.create({
                      title: 'Logged out',
                      description: 'You have been signed out.',
                      type: 'info',
                      duration: 3000,
                      closable: true,
                    });
                  });
                }}
              >
                Log out
              </Button>
            </HStack>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Sidebar toggle position
            </Text>
            <Text fontSize="xs" color="gray.500" mb={3}>
              Where the menu button and sidebar appear
            </Text>
            <SimpleGrid columns={2} gap={2}>
              {SIDEBAR_TOGGLE_POSITIONS.map((p) => {
                const selected = config.sidebarTogglePosition === p;
                const label = p
                  .split('-')
                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                  .join(' ');
                return (
                  <Button
                    key={p}
                    size="sm"
                    variant={selected ? 'solid' : 'outline'}
                    colorScheme={selected ? 'blue' : 'gray'}
                    onClick={() => {
                      setSidebarTogglePosition(p as SidebarTogglePosition);
                      toaster.create({
                        title: 'Updated',
                        description: `Sidebar toggle: ${label}`,
                        type: 'success',
                        duration: 2000,
                        closable: true,
                      });
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </SimpleGrid>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Recent documents
            </Text>
            <Text fontSize="xs" color="gray.500" mb={2}>
              Clear the list of recently opened documents shown in the sidebar
            </Text>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              onClick={() => {
                clearField('recentlyOpenedDocuments');
                toaster.create({
                  title: 'Cleared',
                  description: 'Recent documents list has been cleared.',
                  type: 'success',
                  duration: 3000,
                  closable: true,
                });
              }}
            >
              Clear recent documents
            </Button>
          </Box>

          {import.meta.env.DEV && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                API Endpoint
              </Text>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Choose your API endpoint for requests
              </Text>
              <HStack gap={3}>
                <Button
                  colorScheme={selectedEndpoint === 'remote' ? 'blue' : 'gray'}
                  variant={selectedEndpoint === 'remote' ? 'solid' : 'outline'}
                  onClick={() => handleEndpointChange('remote')}
                  size="sm"
                  flex={1}
                >
                  Remote
                </Button>
                <Button
                  colorScheme={selectedEndpoint === 'local' ? 'blue' : 'gray'}
                  variant={selectedEndpoint === 'local' ? 'solid' : 'outline'}
                  onClick={() => handleEndpointChange('local')}
                  size="sm"
                  flex={1}
                >
                  Local
                </Button>
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Current: {config.apiHost}
              </Text>
              <Button
                size="sm"
                variant="outline"
                colorScheme="gray"
                mt={2}
                onClick={() => {
                  const key = config.isLocal ? 'localApiHost' : 'remoteApiHost';
                  clearField(key);
                  toaster.create({
                    title: 'Reset',
                    description: `${config.isLocal ? 'Local' : 'Remote'} API host cleared; using default.`,
                    type: 'info',
                    duration: 3000,
                    closable: true,
                  });
                }}
              >
                Reset
              </Button>
            </Box>
          )}

          <Box borderTop="1px" borderColor="gray.200" />

          <HStack justify="space-between" align="center">
            <Text fontSize="sm" fontWeight="medium">
              Auto-save prompts
            </Text>
            <Switch.Root
              checked={settings.autoSave}
              onCheckedChange={(e) =>
                setSettings({ ...settings, autoSave: e.checked })
              }
            />
          </HStack>

          <HStack justify="space-between" align="center">
            <Text fontSize="sm" fontWeight="medium">
              Dark mode
            </Text>
            <Switch.Root
              checked={settings.darkMode}
              onCheckedChange={(e) =>
                setSettings({ ...settings, darkMode: e.checked })
              }
            />
          </HStack>

          <HStack justify="space-between" align="center">
            <Text fontSize="sm" fontWeight="medium">
              Notifications
            </Text>
            <Switch.Root
              checked={settings.notifications}
              onCheckedChange={(e) =>
                setSettings({ ...settings, notifications: e.checked })
              }
            />
          </HStack>

          <Button
            colorScheme="blue"
            onClick={handleSave}
            alignSelf="flex-start"
          >
            Save Settings
          </Button>
        </VStack>
      </Box>
    </Box>
  );
}
